import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { Plus, X, Building2, Beaker, Layers, Map as MapIcon, Search } from 'lucide-react';
import { CANONICAL_SUBSEGMENTS, MARKET_NAMES, type MarketCode } from '@hc/shared';
import { PanelFrame } from '../panel-frame.js';
import { PanelEmptyState } from '../panel-empty-state.js';
import { clusterByGroup, GroupHeader, GROUP_MEMBER_INDENT } from '../entity-search-grouping.js';
import { useOpenChannelPanel } from '../use-open-channel-panel.js';
import { buttonStyle } from '../button.js';
import type { PanelParams } from '../components.js';
import { useCatchmentLink, useCatchmentKey, useCatchmentOwned } from '../catchment-link.js';
import { useRollupLink } from '../rollup-link.js';
import { useSelectLink } from '../select-link.js';
import { useBoltOnMulti } from '../../api/bolt-on-hooks.js';
import type { BoltOnMultiQuery } from '../../api/client.js';
import { useGlobalCurrency } from '../store.js';
import {
  convertCurrency,
  convertFromEur,
  CURRENCY_SYMBOL,
  GLOBAL_CURRENCIES,
  type GlobalCurrency,
} from '../../currency.js';
import { useScreenerFacets } from '../../api/screener-hooks.js';
import { formatMoney, formatCount, EMPTY_VALUE } from '../../format.js';
import {
  useRollupFoundations,
  useCreateRollupFoundation,
  useRollup,
  useAddSyntheticMember,
  useDeleteSyntheticMember,
  useRemoveEntityFromWatchlist,
  useAddEntityToWatchlist,
} from '../../api/hooks.js';
import { listEntities } from '../../api/client.js';
import type { RollupMember, RollupCombine, RollupConcentration } from '../../api/client.js';

// Roll-Up Simulator — v1 (foundation + pro-forma combine). The buy-list is a
// "Roll-Up Foundation" (a watchlist of kind 'rollup'); members are real
// registry entities (added from the Screener via the watchlist menu) plus
// user-defined SYNTHETIC off-market targets entered here. The pro-forma combine
// sums both. Concentration impact, the coverage map and the combined catchment
// land in later phases.

function money(eur: number | null | undefined, ccy: GlobalCurrency): string {
  if (eur == null) return EMPTY_VALUE;
  return formatMoney(convertFromEur(eur, ccy), { currency: CURRENCY_SYMBOL[ccy] });
}

/** A closed ring of [lng,lat] points approximating a circle (equirectangular).
 *  Only used as a fallback when a member's drive-time isochrone didn't resolve. */
function circleRing(lat: number, lng: number, radiusKm: number, points = 24): number[][] {
  const dLat = radiusKm / 110.574;
  const dLng = radiusKm / (111.32 * Math.cos((lat * Math.PI) / 180) || 1e-6);
  const ring: number[][] = [];
  for (let i = 0; i <= points; i++) {
    const t = (2 * Math.PI * i) / points;
    ring.push([lng + dLng * Math.cos(t), lat + dLat * Math.sin(t)]);
  }
  return ring;
}

export function RollupSimulatorPanel({ tabId, panelId }: PanelParams) {
  const ccy = useGlobalCurrency();
  const foundations = useRollupFoundations();
  const createFoundation = useCreateRollupFoundation();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Default to the first foundation once they load.
  useEffect(() => {
    const list = foundations.data?.data ?? [];
    if (!selectedId && list.length > 0) setSelectedId(list[0]!.id);
  }, [foundations.data, selectedId]);

  const catchKey = useCatchmentKey(tabId, panelId);
  // Select a foundation on request from this channel (the Prescription panel's
  // "Commit to Roll-Up Foundation" drives the linked Roll-Up to the foundation it
  // just created — or a freshly-opened one reads it on mount). Runs AFTER the
  // auto-select above so it wins.
  const selReq = useSelectLink((s) => s.byKey[catchKey] ?? null);
  useEffect(() => {
    if (selReq && selReq.kind === 'foundation') setSelectedId(selReq.id);
  }, [selReq?.nonce, selReq?.kind, selReq?.id]);

  const rollup = useRollup(selectedId);
  const list = foundations.data?.data ?? [];
  const openCatchment = useOpenChannelPanel(tabId, panelId, 'bolt-on', 'Catchment');
  const members = rollup.data?.members;
  const concentration = rollup.data?.concentration ?? null;

  // One coverage point per SITE across every member — the TRUE combined
  // footprint (a multi-site operator contributes all its sites, not one point).
  // Site-less members were placed at their region centroid server-side, so every
  // member still appears. Capped at the /bolt-on/multi ceiling.
  const coveragePts = useMemo(
    () =>
      (members ?? [])
        .filter((m) => m.subsegment)
        .flatMap((m) =>
          m.siteCoords.map((c) => ({
            lat: c.lat,
            lng: c.lng,
            name: m.name,
            subsegment: m.subsegment as string,
            synthetic: m.kind === 'synthetic',
            entityId: m.kind === 'synthetic' ? null : m.id,
            countryCode: m.countryCode,
          })),
        )
        .slice(0, 80),
    [members],
  );

  // The combined catchment is the union of each member's REAL subsegment×urbanity
  // catchment — the same drive-time isochrone the Bolt-On panel draws, NOT a flat
  // circle. Census markets are GB/FR, so scope the catchment to the dominant
  // country (the pin layer below still shows every member).
  const market = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of coveragePts)
      if (p.countryCode) counts[p.countryCode] = (counts[p.countryCode] ?? 0) + 1;
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'GB';
  }, [coveragePts]);
  const multiQuery = useMemo<BoltOnMultiQuery | null>(() => {
    const inMarket = coveragePts.filter((p) => p.countryCode === market);
    if (inMarket.length === 0) return null;
    return {
      members: inMarket.map((p) => ({ lat: p.lat, lng: p.lng, subsegment: p.subsegment })),
    };
  }, [coveragePts, market]);
  const multi = useBoltOnMulti(multiQuery);
  const multiMembers = multi.data?.members;

  // Broadcast the combined catchment (union of per-member isochrones) to this
  // panel's channel; a Census panel on the same channel profiles the combined
  // population. The Bolt-On panel stands down in coverage mode — this panel owns
  // the channel's catchment.
  const setCatchment = useCatchmentLink((s) => s.setFor);
  // While a Catchment panel is open in coverage mode it OWNS the channel's
  // catchment (broadcasting its travel-adjusted version). Stand down then, so we
  // don't clobber the user's travel/band choice with our fixed "Auto" catchment.
  // The ref lets the cleanup respect ownership at teardown time (a stale closure
  // would otherwise null out a catchment the Catchment panel still owns).
  const catchmentOwned = useCatchmentOwned(catchKey);
  const catchmentOwnedRef = useRef(catchmentOwned);
  catchmentOwnedRef.current = catchmentOwned;
  useEffect(() => {
    if (catchmentOwned) return;
    const cats = (multiMembers ?? []).flatMap((m) => (m.catchment ? [m.catchment] : []));
    if (cats.length === 0) {
      setCatchment(catchKey, null);
      return;
    }
    // Each member's isochrone outline (Leaflet [lat,lng] → GeoJSON [lng,lat]); a
    // member whose isochrone didn't resolve falls back to its radius circle.
    const coordinates = cats.map((c) => {
      const b = c.boundary;
      const ring =
        b.polygon && b.polygon.length >= 3
          ? b.polygon.map(([lat, lng]) => [lng, lat] as number[])
          : circleRing(b.lat, b.lng, b.radiusKm);
      return [ring];
    });
    setCatchment(catchKey, {
      polygon: { type: 'MultiPolygon', coordinates },
      market,
      region: null,
      label: `Roll-up · ${cats.length} catchment${cats.length === 1 ? '' : 's'}`,
    });
    return () => {
      if (!catchmentOwnedRef.current) setCatchment(catchKey, null);
    };
  }, [multiMembers, market, catchKey, setCatchment, catchmentOwned]);

  // Broadcast the combine's lens (one point per member + primary subsegment) on
  // the same channel: the Map renders the members as a coverage pin layer; the
  // Consolidators panel offers to focus white-space on the subsegment.
  const setRollupLink = useRollupLink((s) => s.setFor);
  useEffect(() => {
    if (coveragePts.length === 0) {
      setRollupLink(catchKey, null);
      return;
    }
    setRollupLink(catchKey, {
      points: coveragePts.map((p) => ({
        lat: p.lat,
        lng: p.lng,
        name: p.name,
        synthetic: p.synthetic,
        subsegment: p.subsegment,
        entityId: p.entityId,
      })),
      subsegment: concentration?.market.subsegment ?? null,
      country: concentration?.market.country ?? null,
      label: `Roll-up · ${coveragePts.length} site${coveragePts.length === 1 ? '' : 's'}`,
    });
    return () => setRollupLink(catchKey, null);
  }, [coveragePts, concentration, catchKey, setRollupLink]);

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--s-3)',
          padding: 'var(--s-3)',
          height: '100%',
          overflow: 'auto',
        }}
      >
        <FoundationBar
          list={list}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onCreate={async (name) => {
            const res = await createFoundation.mutateAsync(name);
            setSelectedId(res.data.id);
          }}
          creating={createFoundation.isPending}
          onOpenCatchment={selectedId ? openCatchment : undefined}
        />

        {!selectedId ? (
          <PanelEmptyState icon={<Layers size={18} />} title="Model a buy-and-build combine">
            Create a Roll-Up Foundation above, then add real targets from <b>Triage</b>
            (right-click → Add to list) or enter off-market ones — watch the pro-forma and CR5/HHI
            concentration update as you build.
          </PanelEmptyState>
        ) : rollup.isLoading ? (
          <Empty>Loading combine…</Empty>
        ) : rollup.data ? (
          <>
            <ProForma combine={rollup.data.combine} ccy={ccy} />
            <Concentration
              national={rollup.data.concentration}
              regional={rollup.data.concentrationRegional}
            />
            <Members members={rollup.data.members} ccy={ccy} watchlistId={selectedId} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
              <AddExisting
                watchlistId={selectedId}
                existingIds={
                  new Set(rollup.data.members.filter((m) => m.kind === 'real').map((m) => m.id))
                }
              />
              <AddSynthetic watchlistId={selectedId} />
            </div>
          </>
        ) : (
          <Empty>Couldn’t load this foundation.</Empty>
        )}
      </div>
    </PanelFrame>
  );
}

// --- foundation selector ----------------------------------------------------

function FoundationBar({
  list,
  selectedId,
  onSelect,
  onCreate,
  creating,
  onOpenCatchment,
}: {
  list: Array<{ id: string; name: string; entityCount: number }>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreate: (name: string) => void;
  creating: boolean;
  /** Open (or focus) a Catchment panel on this Roll-Up's channel; undefined hides
   *  the button (no foundation selected → nothing to profile yet). */
  onOpenCatchment?: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-secondary)' }}>Foundation</span>
      <select
        value={selectedId ?? ''}
        onChange={(e) => onSelect(e.target.value)}
        style={selectStyle}
      >
        {list.length === 0 && <option value="">No foundations yet</option>}
        {list.map((f) => (
          <option key={f.id} value={f.id}>
            {f.name} ({f.entityCount})
          </option>
        ))}
      </select>
      {adding ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) {
              onCreate(name.trim());
              setName('');
              setAdding(false);
            }
          }}
          style={{ display: 'flex', gap: 'var(--s-1)' }}
        >
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Foundation name"
            style={inputStyle}
          />
          <button type="submit" disabled={creating} style={btnStyle}>
            Create
          </button>
          <button type="button" onClick={() => setAdding(false)} style={btnGhostStyle}>
            Cancel
          </button>
        </form>
      ) : (
        <button type="button" onClick={() => setAdding(true)} style={btnStyle}>
          <Plus size={12} /> New foundation
        </button>
      )}
      {onOpenCatchment && (
        <button
          type="button"
          onClick={onOpenCatchment}
          style={btnStyle}
          title="Open a Catchment panel on this channel to profile the combined catchment (focuses the existing one if already open)"
        >
          <MapIcon size={12} /> Catchment
        </button>
      )}
    </div>
  );
}

// --- pro-forma combine ------------------------------------------------------

function ProForma({ combine, ccy }: { combine: RollupCombine; ccy: GlobalCurrency }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 'var(--s-2)',
        }}
      >
        <Stat
          label="Combined revenue"
          value={money(combine.revenueEur, ccy)}
          strong
          // ⚠️ Say how much of this is a model. For GB residential care 89.6% of
          // revenue is Estimated, Σ(estimated) is known to run SHORT, and GB is
          // the market where no level correction could be trusted. A user reading
          // a pro-forma needs to know whether they are reading accounts.
          sub={(() => {
            const est = combine.revenueEstimatedEur ?? null;
            if (est == null || combine.revenueEur <= 0) return undefined;
            const pct = Math.round((100 * est) / combine.revenueEur);
            if (pct === 0) return 'from filed accounts';
            return `${pct}% modelled — estimates run low in aggregate`;
          })()}
        />
        <Stat
          label="Operators"
          value={String(combine.opcos)}
          sub={`${combine.realCount} real · ${combine.syntheticCount} off-market`}
        />
        <Stat label="Employees" value={formatCount(combine.employees)} />
        <Stat label="Sites" value={formatCount(combine.sites)} />
        <Stat
          label="Sub-segments"
          value={String(combine.subsegments.length)}
          sub={combine.subsegments.join(', ') || '—'}
        />
        <Stat
          label="Regions"
          value={String(combine.regions.length)}
          sub={combine.regions.join(', ') || '—'}
        />
      </div>
      {combine.ownedCount > 0 && (
        <div
          style={{
            fontSize: 'var(--t-meta)',
            color: 'var(--color-warning-text, #854f0b)',
            background: 'var(--color-warning-bg, #faeeda)',
            border: '1px solid var(--hairline)',
            borderRadius: 4,
            padding: '6px 10px',
          }}
        >
          {combine.ownedCount} member{combine.ownedCount > 1 ? 's are' : ' is'} already part of a
          consolidator group — not acquirable as independent{combine.ownedCount > 1 ? 's' : ''}.
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  strong,
}: {
  label: string;
  value: string;
  sub?: string;
  strong?: boolean;
}) {
  // Match the platform KPI-card style (e.g. Census .cs-kpi): white --menu-surface
  // fill so cards read against the cream panel body, hairline border, radius-sm,
  // uppercase meta label. Previously used --surface (= the panel body colour),
  // so the cards blended in.
  return (
    <div
      style={{
        background: 'var(--menu-surface)',
        border: '1px solid var(--hairline)',
        borderRadius: 'var(--radius-sm)',
        padding: '9px 11px',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 'var(--t-meta)',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          color: 'var(--text-tertiary)',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: strong ? '18px' : '15px',
          fontWeight: 700,
          letterSpacing: '-0.015em',
          lineHeight: 1.1,
          color: 'var(--text-primary)',
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          style={{
            fontSize: 'var(--t-meta)',
            color: 'var(--text-tertiary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={sub}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

// --- market-structure impact ------------------------------------------------

function Concentration({
  national,
  regional,
}: {
  national: RollupConcentration | null;
  regional: (RollupConcentration & { thin: boolean }) | null;
}) {
  // Default to the REGIONAL view when we have one: a buy-list is a rounding error
  // against a whole national market (national before≈after reads as "broken"),
  // whereas the local market is where a roll-up actually moves concentration.
  const [scope, setScope] = useState<'national' | 'regional'>(regional ? 'regional' : 'national');
  if (!national) {
    return (
      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
        Add targets with a sub-segment and revenue to model market-structure impact.
      </div>
    );
  }
  const showRegional = scope === 'regional' && !!regional;
  const data = showRegional ? regional! : national;
  const pct = (v: number) => `${(v * 100).toFixed(1)}%`;
  const dHhi = data.hhiAfter - data.hhiBefore;
  // DOJ/EC 2010 HHI bands — anchor the raw 0–10,000 number so a value like "18"
  // reads as "unconcentrated", not "wrong".
  const hhiBand = (h: number) =>
    h < 1500 ? 'unconcentrated' : h <= 2500 ? 'moderately concentrated' : 'highly concentrated';
  const scopeLabel = showRegional ? (data.region ?? data.market.country) : data.market.country;
  const btn = (s: 'national' | 'regional', label: string) => (
    <button
      type="button"
      onClick={() => setScope(s)}
      style={{
        font: 'inherit',
        fontSize: 'var(--t-meta)',
        padding: '1px 8px',
        borderRadius: 4,
        border: '1px solid var(--hairline)',
        cursor: 'pointer',
        background: scope === s ? 'var(--accent)' : 'transparent',
        color: scope === s ? '#fff' : 'var(--text-secondary)',
      }}
    >
      {label}
    </button>
  );
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--hairline)',
        borderRadius: 6,
        padding: '10px 12px',
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
      >
        <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>Market-structure impact</div>
        {regional && (
          <div style={{ display: 'flex', gap: 4 }}>
            {btn('national', 'National')}
            {btn('regional', 'Region')}
          </div>
        )}
      </div>
      <div style={{ fontSize: 'var(--t-meta)', color: 'var(--text-secondary)', marginBottom: 8 }}>
        {data.market.subsegment} · {scopeLabel} · {data.groupsBefore} groups today
        {showRegional && regional!.thin ? ' · thin sample' : ''}
      </div>
      <BeforeAfter
        label="Roll-up position"
        value={`#${data.rollupRank} group · ${pct(data.rollupShare)} share`}
      />
      <BeforeAfter
        label="CR5 (top-5 share)"
        before={pct(data.cr5Before)}
        after={pct(data.cr5After)}
      />
      <BeforeAfter
        label="HHI"
        before={String(data.hhiBefore)}
        after={`${data.hhiAfter} (${dHhi >= 0 ? '+' : ''}${dHhi})`}
      />
      <div
        style={{
          fontSize: 'var(--t-meta)',
          color: 'var(--text-tertiary)',
          marginTop: 6,
          lineHeight: 1.4,
        }}
      >
        HHI on 0–10,000 (DOJ/EC): this market is {hhiBand(data.hhiAfter)} after. Measured on our
        ownership data, so it understates concentration hidden behind retained brand names.
      </div>
    </div>
  );
}

function BeforeAfter({
  label,
  value,
  before,
  after,
}: {
  label: string;
  value?: string;
  before?: string;
  after?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 'var(--s-2)',
        padding: '3px 0',
        fontSize: 'var(--t-body)',
      }}
    >
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-mono)' }}>
        {value ?? (
          <>
            <span style={{ color: 'var(--text-tertiary)' }}>{before}</span>
            <span style={{ color: 'var(--text-tertiary)', margin: '0 6px' }}>→</span>
            <span style={{ fontWeight: 600 }}>{after}</span>
          </>
        )}
      </span>
    </div>
  );
}

// --- members ----------------------------------------------------------------

function Members({
  members,
  ccy,
  watchlistId,
}: {
  members: RollupMember[];
  ccy: GlobalCurrency;
  watchlistId: string;
}) {
  const qc = useQueryClient();
  const removeReal = useRemoveEntityFromWatchlist();
  const delSynthetic = useDeleteSyntheticMember();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['rollup', watchlistId] });

  if (members.length === 0) {
    return (
      <Empty>
        No targets yet. Add real companies from <b>Triage</b> (right-click → Add to list), or
        off-market targets below.
      </Empty>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {members.map((m) => (
        <div
          key={`${m.kind}:${m.id}`}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--s-2)',
            padding: '5px 0',
            borderBottom: '1px solid var(--hairline)',
            fontSize: 'var(--t-body)',
          }}
        >
          <span title={m.kind === 'synthetic' ? 'Off-market (synthetic)' : 'Registry entity'}>
            {m.kind === 'synthetic' ? <Beaker size={12} /> : <Building2 size={12} />}
          </span>
          <span
            style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {m.name ?? EMPTY_VALUE}
            {m.ownedBy && (
              <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--t-meta)' }}>
                {' '}
                · owned by {m.ownedBy}
              </span>
            )}
          </span>
          <span
            style={{
              color: 'var(--text-secondary)',
              fontSize: 'var(--t-meta)',
              width: 120,
              textAlign: 'right',
            }}
          >
            {m.subsegment ?? EMPTY_VALUE}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', width: 90, textAlign: 'right' }}>
            {money(m.turnoverEur, ccy)}
          </span>
          <span
            title="Revenue confidence (P-value; P100 = reported). Off-market targets have no modelled confidence."
            style={{
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-tertiary)',
              fontSize: 'var(--t-meta)',
              width: 44,
              textAlign: 'right',
            }}
          >
            {m.revenueConfidence ?? EMPTY_VALUE}
          </span>
          <button
            type="button"
            aria-label="Remove"
            onClick={() => {
              if (m.kind === 'synthetic') {
                delSynthetic.mutate({ watchlistId, memberId: m.id });
              } else {
                removeReal.mutate({ watchlistId, entityId: m.id }, { onSuccess: invalidate });
              }
            }}
            style={btnGhostStyle}
          >
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

// --- add synthetic ----------------------------------------------------------

// Add an EXISTING company to the foundation by searching the whole entity
// universe by name / registry id — no trip to Triage required. Distinct from
// "Add off-market target" (which mints a brand-new synthetic). Adds it as a real
// member via the watchlist-membership mutation (which already refreshes the
// combine); companies already in the foundation are shown but disabled.
function AddExisting({
  watchlistId,
  existingIds,
}: {
  watchlistId: string;
  existingIds: Set<string>;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const add = useAddEntityToWatchlist();
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), 200);
    return () => window.clearTimeout(id);
  }, [query]);

  const ready = debounced.length >= 2;
  const results = useQuery({
    queryKey: ['rollup-add-existing', debounced],
    queryFn: () => listEntities({ q: debounced, limit: 12 }),
    enabled: open && ready,
  });
  const rows = ready ? (results.data?.data ?? []) : [];
  const showMenu = open && ready;

  // Anchor the results dropdown under the field + close on outside-click / Escape;
  // keep it pinned to the field on scroll/resize. Mirrors the PanelSelect popover
  // so the menu escapes the (possibly floating) panel's clipping.
  function close() {
    setOpen(false);
    setQuery('');
  }
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = fieldRef.current?.getBoundingClientRect();
      if (r) setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    place();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (fieldRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{ ...btnStyle, alignSelf: 'flex-start' }}
      >
        <Search size={12} /> Add existing company
      </button>
    );
  }
  return (
    <div
      ref={fieldRef}
      style={{
        display: 'flex',
        gap: 'var(--s-2)',
        alignItems: 'center',
        flex: '1 1 260px',
        minWidth: 220,
      }}
    >
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search existing companies by name / registry id…"
        style={{ ...inputStyle, flex: 1 }}
        autoFocus
      />
      <button type="button" onClick={close} style={btnGhostStyle}>
        Done
      </button>
      {showMenu &&
        rect &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: rect.top,
              left: rect.left,
              minWidth: rect.width,
              maxWidth: Math.max(rect.width, 340),
              maxHeight: 300,
              overflowY: 'auto',
              background: 'var(--menu-surface)',
              border: '1px solid var(--hairline)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08)',
              zIndex: 9000,
              padding: 4,
              fontSize: 'var(--t-body)',
            }}
          >
            {results.isLoading ? (
              <Hint>Searching…</Hint>
            ) : rows.length === 0 ? (
              <Hint>No matching companies.</Hint>
            ) : (
              clusterByGroup(rows, (e) => e.economicGroup).clusters.map((cluster) => (
                <div key={cluster.group ?? `solo-${cluster.rows[0]!.id}`}>
                  {cluster.group && (
                    <GroupHeader group={cluster.group} count={cluster.rows.length} />
                  )}
                  {cluster.rows.map((e) => {
                    const already = existingIds.has(e.id);
                    return (
                      <div
                        key={e.id}
                        role="menuitem"
                        onClick={() => {
                          if (already || add.isPending) return;
                          add.mutate({ watchlistId, entityId: e.id });
                          close();
                        }}
                        onMouseOver={(ev) => {
                          if (!already) ev.currentTarget.style.background = 'var(--bg-sunken)';
                        }}
                        onMouseOut={(ev) => {
                          ev.currentTarget.style.background = 'transparent';
                        }}
                        title={already ? 'Already in this foundation' : 'Add to this foundation'}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          padding: '4px 6px',
                          paddingLeft: cluster.group ? 6 + GROUP_MEMBER_INDENT : 6,
                          cursor: already ? 'default' : 'pointer',
                          borderRadius: 'var(--radius-sm)',
                          opacity: already ? 0.55 : 1,
                        }}
                      >
                        <span style={{ color: 'var(--text-primary)' }}>
                          {e.legalName ?? '(no name)'}
                          {already ? ' · ✓ in foundation' : ''}
                        </span>
                        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
                          {[e.countryCode, e.registryId, e.subSegment ?? '—']
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

function Hint({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)', padding: '4px 6px' }}>
      {children}
    </div>
  );
}

function AddSynthetic({ watchlistId }: { watchlistId: string }) {
  const add = useAddSyntheticMember();
  const globalCcy = useGlobalCurrency();
  const facets = useScreenerFacets();
  const regionsByCountry = facets.data?.regionsByCountry ?? {};
  const countryOptions = Object.keys(regionsByCountry).length
    ? Object.keys(regionsByCountry).sort()
    : ['GB', 'FR'];
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [subsegment, setSubsegment] = useState('');
  const [country, setCountry] = useState('GB');
  const [region, setRegion] = useState('');
  const [locator, setLocator] = useState('');
  const [revenue, setRevenue] = useState('');
  // Revenue entry currency defaults to the global display currency set now.
  const [revCcy, setRevCcy] = useState<GlobalCurrency>(globalCcy);
  const [employees, setEmployees] = useState('');
  const regionOptions = regionsByCountry[country] ?? [];

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    add.mutate(
      {
        watchlistId,
        input: {
          name: name.trim(),
          subsegment: subsegment || null,
          countryCode: country || null,
          region: region || null,
          locator: locator.trim() || null,
          // Stored in EUR (turnover_eur basis) — convert from the entry currency.
          turnoverEur: revenue ? Math.round(convertCurrency(Number(revenue), revCcy, 'EUR')) : null,
          employees: employees ? Math.round(Number(employees)) : null,
        },
      },
      {
        onSuccess: () => {
          setName('');
          setSubsegment('');
          setRegion('');
          setLocator('');
          setRevenue('');
          setEmployees('');
          setOpen(false);
        },
      },
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{ ...btnStyle, alignSelf: 'flex-start' }}
      >
        <Plus size={12} /> Add off-market target
      </button>
    );
  }
  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 'var(--s-2)',
        border: '1px solid var(--hairline)',
        borderRadius: 6,
        padding: 'var(--s-2)',
        background: 'var(--surface)',
      }}
    >
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nickname *"
        title="An internal nickname — no sensitive / identifying detail needed."
        style={inputStyle}
        autoFocus
      />
      <select
        value={country}
        onChange={(e) => {
          setCountry(e.target.value);
          setRegion(''); // region options follow the country
        }}
        style={selectStyle}
      >
        {countryOptions.map((c) => (
          <option key={c} value={c}>
            {MARKET_NAMES[c as MarketCode] ?? c}
          </option>
        ))}
      </select>
      <select value={region} onChange={(e) => setRegion(e.target.value)} style={selectStyle}>
        <option value="">Region…</option>
        {regionOptions.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>
      <select
        value={subsegment}
        onChange={(e) => setSubsegment(e.target.value)}
        style={selectStyle}
      >
        <option value="">Sub-segment…</option>
        {CANONICAL_SUBSEGMENTS.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <input
        value={locator}
        onChange={(e) => setLocator(e.target.value)}
        placeholder="Postcode area (optional)"
        title="Optional — places the map pin precisely (e.g. BL1). Otherwise the region centroid is used."
        style={inputStyle}
      />
      <div style={{ display: 'flex', gap: 'var(--s-1)' }}>
        <select
          value={revCcy}
          onChange={(e) => setRevCcy(e.target.value as GlobalCurrency)}
          style={{ ...selectStyle, flex: '0 0 auto', width: 70 }}
          aria-label="Revenue currency"
        >
          {GLOBAL_CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {CURRENCY_SYMBOL[c]} {c}
            </option>
          ))}
        </select>
        <input
          value={revenue}
          onChange={(e) => setRevenue(e.target.value)}
          placeholder="Revenue"
          type="number"
          min="0"
          style={{ ...inputStyle, flex: 1, minWidth: 0 }}
        />
      </div>
      <input
        value={employees}
        onChange={(e) => setEmployees(e.target.value)}
        placeholder="Employees"
        type="number"
        min="0"
        style={inputStyle}
      />
      <div style={{ display: 'flex', gap: 'var(--s-1)' }}>
        <button type="submit" disabled={add.isPending || !name.trim()} style={btnStyle}>
          Add
        </button>
        <button type="button" onClick={() => setOpen(false)} style={btnGhostStyle}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// --- bits -------------------------------------------------------------------

function Empty({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 'var(--t-body)',
        color: 'var(--text-secondary)',
        padding: 'var(--s-3) 0',
        lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  );
}

const selectStyle: CSSProperties = {
  fontSize: 'var(--t-body)',
  padding: '2px var(--s-2)',
  border: '1px solid var(--hairline)',
  borderRadius: 2,
  background: 'var(--menu-surface)',
  color: 'var(--text-primary)',
};
const inputStyle: CSSProperties = {
  fontSize: 'var(--t-body)',
  padding: '2px var(--s-2)',
  border: '1px solid var(--hairline)',
  borderRadius: 2,
  background: 'var(--menu-surface)',
  color: 'var(--text-primary)',
};
// Shared button system (see workspace/button.tsx) — secondary = the default
// surface+border control, ghost = the borderless low-emphasis one.
const btnStyle: CSSProperties = buttonStyle('secondary');
const btnGhostStyle: CSSProperties = buttonStyle('ghost');
